import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import DoctorSelector from 'pages/specialist-booking/components/DoctorSelector';

const FeaturedShowcase = ({ onViewDetails }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  const featuredTransformations = [
    {
      id: 'featured-1',
      title: 'Psoriasis Transformation',
      subtitle: 'Complete Healing Package',
      beforeImage: '/assets/images/diseases/psoriasis-hand.jpg',
      afterImage: '/assets/images/diseases/psoriasis-hand.jpg',
      doctor: 'Dr. L. Haresh',
      category: 'Skin Disorders',
      rating: 4.8,
      duration: 'Structured Therapy Program',
      price: 'Based on Severity',
      description: `A comprehensive Ayurvedic psoriasis care program focused on correcting internal doshic imbalance, reducing immune-mediated skin inflammation, and promoting long-term remission. The treatment integrates physician consultation, internal detoxification, targeted external therapies, and diet-lifestyle correction to restore healthy skin function and prevent recurrence.`,
      services: [
        'Ayurvedic Physician Consultation',
        'Panchakarma (Blood Purification Protocol)',
        'External Therapies (Lepas, Oleation, Fomentation, Shirodhara etc.)',
        'Herbal Formulations for Immune Modulation',
        'Personalized Diet & Lifestyle Guidance'
      ],
      tags: ['Psoriasis Care', 'Autoimmune Skin Disorder', 'Skincare Treatment', 'Eczema', 'Scalp Psoriasis', 'Nail Psoriasis'],
      testimonial: {
        comment: "After years of recurring psoriasis, this treatment helped me control flare-ups naturally. The doctors focused on the root cause, not just the skin, and my quality of life has improved significantly.",
        clientName: "Patient Name Withheld",
        date: "October 2024"
      }
    },
    {
      id: 'featured-2',
      title: 'Rheumatoid Arthritis Care',
      subtitle: 'Ayurvedic Auto-Immune Joint Management',
      beforeImage: '/assets/images/diseases/rhematoid-arthritis-hand.jpg',
      afterImage: '/assets/images/diseases/rhematoid-arthritis-hand.jpg',
      doctor: 'Dr. L. Haresh',
      category: 'Auto-Immune Disorders',
      rating: 4.9,
      duration: 'Structured Therapy Program',
      price: 'Based on Severity',
      description: `A comprehensive Ayurvedic treatment protocol for Rheumatoid Arthritis focused on reducing autoimmune-driven inflammation, alleviating joint pain and stiffness, and slowing disease progression. The approach integrates internal medicines, detoxification therapies, external pain-relief procedures, and personalized diet-lifestyle correction to restore joint function and improve quality of life.`,
      services: [
        'Ayurvedic Rheumatology Consultation',
        'Ama-Pachana & Deepana Protocol',
        'Medicated Oil Therapies (Abhyanga, Kizhi)',
        'Herbal Formulations for Immune Modulation',
        'Anti-inflammatory Diet & Lifestyle Guidance'
      ],
      tags: ['Rheumatoid Arthritis', 'Autoimmune Disorder', 'Joint Pain', 'Ayurvedic Management', 'Chronic Inflammation'],
      testimonial: {
        comment: "My joint pain and morning stiffness reduced significantly with consistent treatment. The focus on digestion, immunity, and lifestyle made a real difference.",
        clientName: "Patient Name Withheld",
        date: "August 2024"
      }
    },
    {
      id: 'featured-3',
      title: 'Disc Prolapse Management',
      subtitle: 'Non-Surgical Ayurvedic Spine Care',
      beforeImage: '/assets/images/diseases/disc-prolapse.jpg',
      afterImage: '/assets/images/diseases/disc-prolapse.jpg',
      doctor: 'Dr. L. Haresh',
      category: 'Bone & Joint Disorders',
      rating: 5.0,
      duration: 'Structured Therapy Program',
      price: 'Based on Severity',
      description: `A targeted Ayurvedic treatment program for disc prolapse aimed at relieving nerve compression, reducing inflammation, and strengthening spinal support structures. The protocol emphasizes non-surgical pain relief through external therapies, internal medicines, posture correction, and rehabilitation-oriented care.`,
      services: [
        'Ayurvedic Spine Assessment',
        'Panchakarma (Bone Metabolism Correction Protocol)',
        'External Oil Therapies (Abhyanga, Kati Basti, Kizhi Procedures)',
        'Internal Medicines for Vata Balance',
        'Posture, Activity & Rehabilitation Guidance'
      ],
      tags: ['Disc Prolapse', 'Slip Disc', 'Back Pain', 'Sciatica', 'Ayurvedic Spine Care'],
      testimonial: {
        comment: "The pain radiating to my leg reduced steadily, and I was able to avoid surgery. The therapies and guidance helped me regain confidence in movement.",
        clientName: "Patient Name Withheld",
        date: "September 2024"
      }
    }

  ];

  useEffect(() => {
    if (!isAutoPlaying) return;

    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % featuredTransformations?.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [isAutoPlaying, featuredTransformations?.length]);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % featuredTransformations?.length);
    setIsAutoPlaying(false);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + featuredTransformations?.length) % featuredTransformations?.length);
    setIsAutoPlaying(false);
  };

  const currentTransformation = featuredTransformations?.[currentSlide];
  const handleNavigation = (path) => {
    window.location.href = path;
    setIsMenuOpen(false);
  };
  return (
    <div className="relative mb-12 overflow-hidden rounded-2xl">
      <div className="glass-card p-0">
        <div className="relative h-96 lg:h-[500px]">
          {/* Background Image with Overlay */}
          <div className="absolute inset-0">
            <Image
              src={currentTransformation?.beforeImage}
              alt={currentTransformation?.title}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/40 to-transparent" />
          </div>

          {/* Content Overlay */}
          <div className="relative z-10 h-full flex items-center">
            <div className="max-w-7xl mx-auto px-6 lg:px-8 w-full">
              <div className="max-w-2xl">
                <div className="flex items-center gap-3 mb-4">
                  <span className="px-3 py-1 bg-primary/90 text-white rounded-full text-sm font-medium">
                    Featured Transformation
                  </span>
                  <div className="flex items-center gap-1">
                    <Icon name="Star" size={16} className="text-warning fill-current" />
                    <span className="text-white font-medium">{currentTransformation?.rating}</span>
                  </div>
                </div>

                <h1 className="font-headline text-3xl lg:text-5xl font-bold text-white mb-3">
                  {currentTransformation?.title}
                </h1>

                <p className="text-xl text-white/90 mb-4">
                  {currentTransformation?.subtitle}
                </p>

                <p className="text-white/80 text-lg mb-6 leading-relaxed">
                  {currentTransformation?.description}
                </p>

                <div className="flex flex-wrap items-center gap-6 mb-8">
                  <div className="flex items-center gap-2">
                    <Icon name="User" size={18} className="text-white/70" />
                    <span className="text-white">{currentTransformation?.doctor}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Icon name="Clock" size={18} className="text-white/70" />
                    <span className="text-white">{currentTransformation?.duration}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Icon name="IndianRupee" size={18} className="text-white/70" />
                    <span className="text-white">{currentTransformation?.price}</span>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-4">
                  <Button
                    variant="default"
                    size="lg"
                    onClick={() => onViewDetails(currentTransformation)}
                    iconName="Eye"
                    iconPosition="left"
                    className="cta-morph bg-white text-black hover:bg-white/90"
                  >
                    View Full Transformation
                  </Button>
                  <Button
                    variant="outline"
                    size="lg"
                    onClick={() => handleNavigation('/specialist-booking')}
                    iconName="Calendar"
                    iconPosition="left"
                    className="border-white/30 text-white hover:bg-white/10"
                  >
                    Talk To Specialist
                  </Button>
                </div>
              </div>
            </div>
          </div>

          {/* Navigation Arrows */}
          <Button
            variant="ghost"
            size="icon"
            onClick={prevSlide}
            iconName="ChevronLeft"
            className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-black/30 text-white hover:bg-black/50 backdrop-blur-sm"
          />
          <Button
            variant="ghost"
            size="icon"
            onClick={nextSlide}
            iconName="ChevronRight"
            className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-black/30 text-white hover:bg-black/50 backdrop-blur-sm"
          />

          {/* Slide Indicators */}
          <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 flex gap-2">
            {featuredTransformations?.map((_, index) => (
              <button
                key={index}
                onClick={() => {
                  setCurrentSlide(index);
                  setIsAutoPlaying(false);
                }}
                className={`w-3 h-3 rounded-full transition-all duration-300 ₹{currentSlide === index
                  ? 'bg-white scale-125' : 'bg-white/50 hover:bg-white/70'
                  }`}
              />
            ))}
          </div>

          {/* Auto-play Toggle */}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsAutoPlaying(!isAutoPlaying)}
            iconName={isAutoPlaying ? "Pause" : "Play"}
            className="absolute top-4 right-4 bg-black/30 text-white hover:bg-black/50 backdrop-blur-sm"
          />
        </div>
      </div>
    </div>
  );
};

export default FeaturedShowcase;